import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CloseMethod, ExpandPayload, OpenMethod } from '@chat-emp/shared';

import { LiveDataBridge } from '../data/LiveDataBridge.js';
import { usePendingReplies } from '../hooks/usePendingReplies.js';
import { useUnreadCount } from '../hooks/useUnreadCount.js';
import { useHostUnread } from '../hooks/useHostUnread.js';
import type { OpenRequest } from '../hooks/useStartChatRequest.js';
import { emitToHost } from '../lib/bridge.js';
import { track } from '../lib/analytics.js';
import { useSession } from '../session/SessionProvider.js';
import { StreamProvider } from '../session/StreamProvider.js';
import { Icon } from '../components/Icon.js';
import { plural } from '../util/format.js';
import { Callout } from './Callout.js';
import { WidgetPanel } from './WidgetPanel.js';
import { useCallout } from './useCallout.js';
import { useDrag } from './useDrag.js';
import styles from './ChatWidget.module.css';

interface ChatWidgetProps {
  /**
   * Hand a conversation over to the full chat page.
   *
   * Supplied by the host, because the host owns both halves of it: where its
   * chat page lives, and how one gets there. Expanding leaves the host page,
   * and in the real product it leaves the *package* too — this widget is an npm
   * dependency of someone else's site, so the handoff is a URL for some hosts
   * and an emitted event for others (see the README). Neither belongs baked
   * into a button in here. The ask travels as an argument because nothing
   * else survives the boundary; component state certainly doesn't. It is the
   * `expand` bridge payload it becomes, so whichever surface knows those facts
   * states them once and nothing in between re-assembles them.
   */
  onExpand: (req: ExpandPayload) => void;
  /**
   * Fires whenever the panel opens or closes, and how. The iframe embed uses this
   * to tell the widget.js loader to resize the host iframe (launcher ⇄ panel);
   * without it the panel opens clipped inside the tiny launcher iframe. The
   * `method` is what lets a host tell a nudge-driven open from a launcher tap —
   * `undefined` only for the initial closed state reported on mount, which
   * nobody did anything to cause.
   */
  onOpenChange?: (open: boolean, method?: OpenMethod | CloseMethod) => void;
  /**
   * Show the "N chats need your reply" nudge above the launcher. Defaults OFF
   * for now — the nudge is switched off product-wide until we choose to bring it
   * back; flip the default here to re-enable it everywhere. It stays a prop
   * because hosts that embed us in a frame they size themselves and do not
   * listen for the `callout` event must be able to keep it off regardless: the
   * nudge is taller than a launcher and would render clipped.
   */
  nudge?: boolean;
  /**
   * Gate the open panel on session state — connecting / not-authorized instead of
   * fixtures. On for the real /widget embed; off (default) for /widget-preview,
   * which is a deliberate fixtures demo.
   */
  authGate?: boolean;
  /**
   * A host request to open — on a channel it knows, or onto a target it wants
   * composed. Opening on it is this component's job; producing it is the view's
   * (see `useStartChatRequest`).
   */
  request?: OpenRequest | null;
  /** The request has been dealt with — sent, cancelled, or backed out of. */
  onRequestHandled?: () => void;
}

/**
 * The chat widget: a badge on every host page, and enough chat behind it to
 * answer someone without leaving the page you're on.
 *
 * Scope is reply-only, and the line is drawn at what a 400px panel can hold
 * honestly. Read the list, open a thread, reply, see how long the chat has
 * left. Everything else — the rail, lists, filters, new chat, ended,
 * broadcast, hiring stages, ending a chat — lives at /chat, one expand away.
 * The widget is not a small /chat; it is the reply loop, and /chat is the
 * workspace.
 *
 * Cramming the whole page in at mobile width is what stream-chat does today
 * (`src/widget/Widget.tsx` hardcodes `view-mode: 'mobile'` around the entire
 * app). That worked when the page was a list and a thread. It stopped working
 * when the page grew a five-section rail.
 */
export function ChatWidget({
  onExpand,
  onOpenChange,
  nudge = false,
  authGate,
  request,
  onRequestHandled,
}: ChatWidgetProps) {
  const [open, setOpen] = useState(false);
  const { sessionReady } = useSession();
  /*
   * Two counts, deliberately, where there used to be one.
   *
   * The badge answers "is there anything new?" and the nudge answers "is anyone
   * waiting on you?", and those stopped being the same question the moment
   * reading was allowed to clear one of them. An employer who reads everything
   * and answers nothing has an empty badge and a full nudge — which is the
   * truth, and was previously unsayable.
   *
   * Both are polled from the BFF while the widget is shut, so neither opens a
   * Stream connection and neither bills a MAU. Chats, not messages, on both
   * sides: one candidate sending six messages is one person waiting.
   */
  // One number, two sources: the BFF poll while shut, the panel's live count
  // while open (and held after close until the poll answers again). Reset on
  // open so a previous panel's last word cannot stand in for this one's.
  const polled = useUnreadCount(sessionReady && !open);
  const [live, setLive] = useState<number | null>(null);
  const unread = open ? live : (polled ?? live);
  // Gated on `nudge` too: with the nudge off nothing draws this number, and an
  // ungated poll would still cost a request a minute on every host page.
  const pending = usePendingReplies(nudge && sessionReady && !open);
  const callout = useCallout(pending, open);

  // Signal open/close so an iframe host can resize us (see onOpenChange). Keyed on
  // `open` alone: the callback identity changes on unrelated re-renders (host
  // set-context/theme via useHostProps), and depending on it would re-fire the same
  // open/close — harmless for the host resize but inflating the widget_open/close
  // analytics. Hold the latest callback in a ref so only real transitions fire it.
  //
  // The method rides a ref for the same reason: it is set by whatever caused the
  // transition, and reading it as state would make it a second render trigger for
  // something no one renders. Null until something actually happens, which is what
  // the mount-time "closed" report sends.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const methodRef = useRef<OpenMethod | CloseMethod | null>(null);
  useEffect(() => {
    onOpenChangeRef.current?.(open, methodRef.current ?? undefined);
  }, [open]);

  // The host grows the frame only after it hears `open`, so hold the panel until
  // the frame grows past the height it had at open, else it blinks. Not framed
  // (the preview page), there is no frame to wait for.
  const [frameReady, setFrameReady] = useState(false);
  useLayoutEffect(() => {
    if (!open) return setFrameReady(false);
    if (window.self === window.top) return setFrameReady(true);
    const atOpen = window.innerHeight;
    const ready = () => setFrameReady(true);
    const onResize = () => window.innerHeight > atOpen && ready();
    window.addEventListener('resize', onResize);
    const fallback = setTimeout(ready, 400); // never stuck hidden if no resize comes
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(fallback);
    };
  }, [open]);

  const openWidget = (method: OpenMethod) => {
    methodRef.current = method;
    setLive(null);
    setOpen(true);
  };

  // A host request opens the widget. Keyed on `requestId` so closing and
  // clicking the same Chat button again reopens it — the payload is identical,
  // only the id moved. Reported as `fab` because that is what the employer
  // actually did: they pressed a chat button on the host page.
  useEffect(() => {
    if (request) openWidget('fab');
    // `request` itself is deliberately not a dependency — see above.
  }, [request?.requestId]);
  const closeWidget = (method: CloseMethod) => {
    methodRef.current = method;
    setOpen(false);
  };

  // Tell the loader when the nudge is up so it can grow the launcher iframe to
  // fit it. Without this the callout draws against the edge of a 190x96 frame
  // and is simply cut off — which is why this embed used to switch it off.
  //
  // The measurement travels with the event because only this side can take it:
  // the loader would otherwise be carrying a copy of our layout, and a nudge
  // whose text wrapped would silently outgrow it. Measured after commit, so the
  // callout is in the DOM; the rise animation only translates, never resizes.
  const rootRef = useRef<HTMLDivElement>(null);
  const drag = useDrag(rootRef);
  const nudgeVisible = nudge && callout.visible;
  useEffect(() => {
    const box = nudgeVisible ? rootRef.current?.getBoundingClientRect() : undefined;
    emitToHost('callout', {
      visible: nudgeVisible,
      ...(box ? { width: Math.ceil(box.width), height: Math.ceil(box.height) } : {}),
    });
  }, [nudgeVisible]);

  // The host badges its navbar with what we drew — the one relay for this surface.
  useHostUnread(unread);

  return (
    <div className={styles.root} ref={rootRef} {...drag}>
      {nudgeVisible && (
        <Callout
          count={pending}
          onReply={() => {
            emitToHost('callout-reply', { count: pending });
            track('callout_reply', { count: pending });
            callout.dismiss();
            // Reported before the open, so the host sees `callout-reply` then an
            // `open` carrying `method: 'callout'` — the stream-chat guarantee.
            openWidget('callout');
          }}
          onLater={() => {
            emitToHost('callout-later', { count: pending });
            track('callout_later', { count: pending });
            callout.dismiss();
          }}
        />
      )}

      {/* Mounted only while open, and that gate is the whole optimization:
          `WidgetPanel` is where `useInbox` lives, so a shut widget has loaded no
          channels and — in Stream mode — opened no connection. The badge beside
          it costs a count, not a session. Undo this and every employer who never
          opens the widget still bills as a Stream MAU, on every host page. */}
      {open && (
        <StreamProvider connect>
          {/* No `preload`: the list is the read model's now, hydrated a page at
              a time, and an opened thread hydrates its own channel. Preloading
              every channel here was the widget's own unbounded queryChannels. */}
          <LiveDataBridge />
          <WidgetPanel
            onClose={closeWidget}
            onExpand={onExpand}
            onUnread={setLive}
            gate={authGate}
            request={request}
            onComposeDone={onRequestHandled}
            framePending={!frameReady}
          />
        </StreamProvider>
      )}

      <button
        className={open ? styles.fabOpen : styles.fab}
        data-drag-handle
        onClick={() => (open ? closeWidget('close-pill') : openWidget('fab'))}
        aria-label={open ? 'Close chat' : 'Open chat'}
        aria-expanded={open}
      >
        <Icon name={open ? 'close' : 'chatBubble'} size={20} />
        {!open && <span className={styles.fabLabel}>Chats</span>}
        {!open && unread !== null && unread > 0 && (
          <span className={styles.badge} role="img" aria-label={plural(unread, 'unread chat')}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    </div>
  );
}
